import { DaoModel } from '@dynameat/models';
import i18n from '@core/plugins/i18n';
import { ForecastGroup } from '../../../models/forecastGroup';
import { FrontPrice } from './frontPrice';

export class FrontPriceList extends DaoModel {
  constructor(saleRateGuid, startDate, endDate, groupList, priceList) {
    super(null);
    this.saleRateGuid = saleRateGuid || '';
    this.startDate = startDate || null;
    this.endDate = endDate || null;
    this.groupList = groupList || [];
    this.priceList = priceList || [];
  }

  static toInstance(props = {}) {
    const groupList = ForecastGroup.toInstanceList(props.group_list);
    const priceList = FrontPrice.toInstanceList(props.price_list);
    return new FrontPriceList(props.sale_rate_guid, props.start_date, props.end_date, groupList, priceList);
  }

  serialize() {
    return {
      sale_rate_guid: this.saleRateGuid,
      start_date: this.startDate,
      end_date: this.endDate,
      group_list: this.groupList.map((group) => group.serialize()),
      price_list: this.priceList.map((price) => price.serialize())
    };
  }

  editPrice({ priceGuid, newVal }) {
    this.priceList.find((price) => price.guid === priceGuid)?.changePrice(newVal);
  }

  toTable(services, mainColumnProps, childColumnProps, initialColumn) {
    const rows = [];
    const columns = [];
    const days = new Set();
    const serviceGuids = new Set();

    this.#getRows(rows, days, serviceGuids);
    this.#getColumns(columns, days, serviceGuids, services, mainColumnProps, childColumnProps, initialColumn);
    return { rows, columns };
  }

  #getRows(rows, days, serviceGuids) {
    let currentRow = null;
    let currentProduct = null;
    let price;
    for (let i = 0; i < this.priceList.length; i++) {
      price = this.priceList[i];
      days.add(price.date);
      serviceGuids.add(price.serviceGuid);
      if (currentProduct !== price.productGuid) {
        if (currentRow) {
          rows.push(currentRow);
        }
        currentRow = {
          productName: price.productName,
          productGuid: price.productGuid
        };
        currentProduct = price.productGuid;
      }
      FrontPriceList.#processCell(currentRow, price);
    }
    if (currentRow) {
      rows.push(currentRow);
    }
  }

  static getDateString(dayString) {
    return i18n.d(new Date(dayString), 'long');
  }

  #getColumns(columns, days, serviceGuids, services, mainColumnProps, childColumnProps, initialColumn) {
    const servicesMap = Object.fromEntries(services.map((service) => ([service.guid, service])));
    if (initialColumn) {
      columns.push(initialColumn);
    }
    columns.push(...[...days].map((day) => ({
      name: FrontPriceList.getDateString(day),
      ...mainColumnProps,
      children: [...serviceGuids].map((serviceGuid) => ({
        name: servicesMap[serviceGuid].name,
        prop: `${day}_${serviceGuid}`,
        ...childColumnProps,
      }))
    })));
  }

  static #processCell(currentRow, price) {
    const key = `${price.date}_${price.serviceGuid}`;
    const keyPrev = `${price.date}_${price.serviceGuid}_prev`;
    const keyMin = `${price.date}_${price.serviceGuid}_min`;
    const keyMax = `${price.date}_${price.serviceGuid}_max`;
    const keyGuid = `${price.date}_${price.serviceGuid}_guid`;
    currentRow[key] = price.price;
    currentRow[keyPrev] = price.previousPrice;
    currentRow[keyMin] = price.minPrice;
    currentRow[keyMax] = price.maxPrice;
    currentRow[keyGuid] = price.guid;
  }

  getChangedList() {
    const changedPriceList = this.priceList.filter((el) => el.hasChanged());
    return new FrontPriceList(this.saleRateGuid, this.startDate, this.endDate, this.groupList, changedPriceList);
  }

  getByPriceGuid(priceGuid) {
    return this.priceList.find((el) => el.guid === priceGuid);
  }

  hasPriceChanged(priceGuid, newPrice) {
    const price = this.getByPriceGuid(priceGuid);
    return newPrice !== price.price;
  }
}
